import { ICitation } from '@vivli/shared/features/citations/infrastructure/interface';
import { ColDef, GridApi, RowNode } from 'ag-grid-community';
import { useEffect, useRef, useState } from 'react';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { IStudy } from '@vivli/features/studies/infrastructure/interface';
import { CitationRowRenderer } from './citation-row-renderer.component';
import { GridView } from '@vivli/shared/views';

interface CitationsGridComponentProps {
    citations: ICitation[];
    onGridReady?: (gridApi: GridApi) => void;
    emptyMessage?: string;
    hideFloatingFilter?: boolean;
    hideHeader?: boolean;
    autoHeight?: boolean;
    dataRequest?: IDataRequest;
    dataRequestDoiStem?: string;
    study?: IStudy;
    onChange?: () => void;
}

export const CitationsGridComponent = ({
    citations,
    onGridReady,
    emptyMessage,
    hideFloatingFilter,
    hideHeader,
    autoHeight,
    onChange,
    dataRequestDoiStem,
}: CitationsGridComponentProps) => {
    const [rowData, setRowData] = useState<{ citation: ICitation }[]>();
    const gridApiRef = useRef<GridApi>();

    const handleOnGridReady = (gridApi: GridApi) => {
        onGridReady && onGridReady(gridApi);
        gridApiRef.current = gridApi;
    };

    const handleRemoveRow = (node: RowNode, citation: ICitation) => {
        gridApiRef.current.applyTransaction({ remove: [node.data] });
        onChange && onChange();
    };

    const columnDefs: ColDef[] = [
        {
            field: 'citation',
            headerName: 'Citation',
            filter: true,
            sortable: true,

            cellRenderer: 'CitationRow',
            cellRendererParams: {
                onRemoveRow: handleRemoveRow,
                dataRequestDoiStem: dataRequestDoiStem,
            },
        },
    ];

    const customComponents = [
        {
            name: 'CitationRow',
            component: CitationRowRenderer,
            params: {
                dataRequestDoiStem: dataRequestDoiStem,
            },
        },
    ];

    useEffect(() => {
        if (!citations) {
            return;
        }

        const newRowData = [];

        citations.forEach((citation) => {
            newRowData.push({ citation });
        });
        setRowData(newRowData);
    }, [citations]);

    return (
        <GridView
            rowData={rowData}
            fullWidthColumns={true}
            onGridReady={handleOnGridReady}
            columns={columnDefs}
            emptyMessage={emptyMessage}
            hideFloatingFilter={hideFloatingFilter}
            hideHeader={hideHeader}
            autoHeight={autoHeight}
            customComponents={customComponents}
            rowHeight={95}
            flushPrevious={true}
        />
    );
};
